"use client";

import { ChevronDownIcon } from "@heroicons/react/24/outline";

/**
 * Laptop vertical-space optimizer for the live simulator. On the immersive
 * simulator routes (and only on lg+, where a pointer can hover), this collapses
 * the top nav down to a thin "peek" strip so the conversation/tools panels get
 * the reclaimed height. Hovering the strip — or tab-focusing into the nav —
 * slides the full bar back down as an OVERLAY (absolute, not in flow), so the
 * simulator never reflows or jumps when the nav appears/disappears.
 *
 * Below lg (touch / no hover) or off the simulator, it renders the nav
 * untouched: a collapsed bar a touch user can't hover would trap them.
 */
export default function SimulatorPeekNav({ enabled, children }) {
  if (!enabled) return children;
  return (
    <div data-nav-peek className="group/navpeek lg:relative lg:z-50 lg:h-3">
      {/* Peek handles render BEFORE the nav so the expanded nav (below, same
          stacking context) sits on top of them and they never intercept the
          pointer while the bar is open. lg+ only.

          (1) Thin cream strip across the whole top edge. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 hidden h-3 border-b border-stone-200/70 bg-surface-chrome transition-opacity duration-200 lg:block lg:group-hover/navpeek:opacity-0"
      />
      {/* (2) Taller center "pull-tab" with a down chevron — a bigger, obvious
          hover/discovery target that protrudes a few px below the strip. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-0 hidden -translate-x-1/2 transition-opacity duration-200 lg:block lg:group-hover/navpeek:opacity-0 lg:group-focus-within/navpeek:opacity-0"
      >
        {/* pointer-events-auto so the protruding tab itself is hoverable,
            extending the expand-trigger area below the thin strip. A connected
            rounded-bottom tab (no shadow, so it doesn't read as sunken); the
            orange chevron matches the simulator's accent (Zap / mic / timer) so
            users notice the hidden nav. */}
        <div className="pointer-events-auto flex h-6 w-16 items-end justify-center rounded-b-xl border border-t-0 border-stone-200 bg-white pb-1 text-orange-500 transition-colors hover:bg-orange-50 hover:text-orange-600">
          <ChevronDownIcon className="h-4 w-4" />
        </div>
      </div>
      {/* Full nav: parked just above the viewport when collapsed, slid into
          place on hover/focus. Rendered LAST so it overlays the handles when
          open. The transform is lg-only so smaller screens keep the nav in
          normal flow. */}
      <div className="lg:absolute lg:inset-x-0 lg:top-0 lg:-translate-y-full lg:transition-[transform,box-shadow] lg:duration-200 lg:ease-out motion-reduce:lg:transition-none lg:group-hover/navpeek:translate-y-0 lg:group-hover/navpeek:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.35)] lg:group-focus-within/navpeek:translate-y-0 lg:group-focus-within/navpeek:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.35)]">
        {children}
      </div>
    </div>
  );
}
